import React, { useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const absolutize = (url) => {
  if (!url) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${BACKEND_URL}${url}`;
  return url;
};

const buildSrcset = (raw) => {
  if (!raw) return undefined;
  return raw
    .split(",")
    .map((part) => part.trim())
    .map((part) => {
      const [url, descriptor] = part.split(/\s+/);
      return `${absolutize(url)} ${descriptor || ""}`.trim();
    })
    .join(", ");
};

/**
 * Responsive <picture> wrapper for product images.
 * Falls back to the legacy Unsplash URL if local mockup variants are missing.
 *
 * Props:
 *  - product: product object from /api/products (decorated with image_variants)
 *  - sizes: optional override for the `sizes` attribute
 *  - className: applied to the underlying <img>
 *  - eager: if true, sets loading="eager" + fetchpriority="high" for hero images
 */
export default function ProductImage({ product, sizes, className = "", eager = false, alt, ...rest }) {
  const [errored, setErrored] = useState(false);

  const variants = product?.image_variants;
  const fallbackSrc = absolutize(product?.imageFallback || product?.image_fallback);
  const localSrc = absolutize(product?.image);

  const finalSrc = errored && fallbackSrc ? fallbackSrc : localSrc;
  const webpSrcset = !errored && variants ? buildSrcset(variants.srcset_webp) : undefined;
  const jpegSrcset = !errored && variants ? buildSrcset(variants.srcset_jpeg) : undefined;
  const finalSizes = sizes || variants?.sizes || "(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw";

  return (
    <picture>
      {webpSrcset && <source type="image/webp" srcSet={webpSrcset} sizes={finalSizes} />}
      {jpegSrcset && <source type="image/jpeg" srcSet={jpegSrcset} sizes={finalSizes} />}
      <img
        src={finalSrc}
        alt={alt || product?.name || ""}
        className={className}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        {...(eager ? { fetchpriority: "high" } : {})}
        {...rest}
        onError={() => setErrored(true)}
      />
    </picture>
  );
}
